//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  CampaignSparkCodesInfo,
  CreatorsCampaignListResponse,
  CreatorsCampaignsInfoResponse,
  CreatorsCampaignsWorkflowResponse,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import {
  Comment,
  CommentResponse,
  ContentApprovalContentResponse,
  ContentApprovalCreatorsListResponse,
  SendCommentResponse,
} from '@apis/creators-campaigns/content-approval/contentApprovalApiModel';
import ItemId from '@models/ItemId';
import { isDefined, isNotEmptyObject } from '@utils/filterPredicates';
import { Serialized } from '@utils/types/Serialized';

export const deserializeCreatorsCampaignsInfo = (
  creatorsCampaignsInfo: Serialized<CreatorsCampaignsInfoResponse>,
): CreatorsCampaignsInfoResponse => ({
  ...creatorsCampaignsInfo,
  ...(isDefined(creatorsCampaignsInfo.startDate)
    ? { startDate: new Date(creatorsCampaignsInfo.startDate) }
    : { startDate: undefined }),
  ...(isDefined(creatorsCampaignsInfo.endDate)
    ? { endDate: new Date(creatorsCampaignsInfo.endDate) }
    : { endDate: undefined }),
});

export const deserializeCreatorsCampaignsWorkflow = (
  creatorCampaignsWorkflow: Serialized<CreatorsCampaignsWorkflowResponse>,
): CreatorsCampaignsWorkflowResponse => ({
  ...creatorCampaignsWorkflow,
  contentRights: {
    ...(isDefined(creatorCampaignsWorkflow.contentRights)
      ? {
          ...creatorCampaignsWorkflow.contentRights,
          expiryDate: isDefined(
            creatorCampaignsWorkflow.contentRights.expiryDate,
          )
            ? new Date(creatorCampaignsWorkflow.contentRights.expiryDate)
            : undefined,
        }
      : {}),
  },
});

export const deserializeActiveCreatorsCampaignsList = (
  activeCreatorsCampaignList: Serialized<CreatorsCampaignListResponse>,
): CreatorsCampaignListResponse => ({
  ...activeCreatorsCampaignList,
  data: activeCreatorsCampaignList.data.map((campaign) => ({
    ...campaign,
    ...(isDefined(campaign.startDate)
      ? { startDate: new Date(campaign.startDate) }
      : { startDate: undefined }),
    ...(isDefined(campaign.endDate)
      ? { endDate: new Date(campaign.endDate) }
      : { endDate: undefined }),
  })),
});

export const deserializeContentApprovalCreatorsList = (
  approvalListResponse: Serialized<ContentApprovalCreatorsListResponse>,
): ContentApprovalCreatorsListResponse => ({
  ...approvalListResponse,
  creators: approvalListResponse.creators?.map((creator) => ({
    ...creator,
    ...(isDefined(creator.lastUpdatedAt)
      ? { lastUpdatedAt: new Date(creator.lastUpdatedAt) }
      : { lastUpdatedAt: undefined }),
  })),
});

const deserializeDateField = (value: number | undefined) =>
  value ? new Date(value) : undefined;

const deserializeComment = (
  comment?: Serialized<Comment>,
): Comment | undefined => {
  if (isDefined(comment) && isNotEmptyObject(comment)) {
    return {
      ...comment,
      createdAt: deserializeDateField(comment.createdAt),
      updatedAt: deserializeDateField(comment.updatedAt),
    };
  }
  return undefined;
};

export const deserializeSendCommentResponse = (
  sendCommentResponse: Serialized<SendCommentResponse>,
): SendCommentResponse => {
  return {
    ...sendCommentResponse,
    comment: deserializeComment(sendCommentResponse.comment),
  };
};

export const deserializeContentReviewContentsList = (
  contentReviewContentsList: Serialized<ContentApprovalContentResponse[]>,
): ContentApprovalContentResponse[] => {
  const deserializeCommentResponse = (
    commentResponse?: Serialized<CommentResponse>,
  ): CommentResponse | undefined => {
    if (isDefined(commentResponse) && isNotEmptyObject(commentResponse)) {
      return {
        ...commentResponse,
        comment: deserializeComment(commentResponse.comment),
      };
    }
    return undefined;
  };
  return contentReviewContentsList.map((item) => ({
    content: {
      ...item.content,
      id: item.content?.contentId ? ItemId(item.content.contentId) : undefined, // do not reassign as we need to make sure we use the original content id for content actions (approval, comments)
      createdAt: deserializeDateField(item.content?.createdAt),
      updatedAt: deserializeDateField(item.content?.updatedAt),
      approvedAt: deserializeDateField(item.content?.approvedAt),
    },
    ...(isDefined(item.comments) && item.comments.length > 0
      ? {
          comments: item.comments
            .map((comment) => deserializeCommentResponse(comment))
            .filter(isDefined),
        }
      : {}),
  }));
};

export const deserializeCampaignSparkCodeResponse = (
  response: Serialized<CampaignSparkCodesInfo>,
): CampaignSparkCodesInfo => ({
  ...response,
  linkRequest: {
    ...response.linkRequest,
    lastRemindedAt: deserializeDateField(
      response.linkRequest?.lastRemindedAt)
  }
});
